import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import bitmapFonts from 'src/lib/bitmapFonts';
import View from '@play-co/timestep-core/lib/ui/View';
import sounds from 'src/lib/sounds';
import Scroll from '../components/shared/Scroll';
import {
  Color,
  ImageScaleView,
  MovieClip,
  MultiplyFilter,
} from '@play-co/timestep-core/ui';
import { PetEvolution, PetSpeciesID } from 'src/replicant/pet';
import * as Pet from 'src/replicant/pet';
import { FlexBoxView, getFlexNode } from '@play-co/timestep-core/flexbox';
import {
  animDuration,
  getCreativeSuffix,
  waitForItPromise,
} from 'src/lib/utils';
import * as Encyclopedia from 'src/replicant/encyclopedia';
import StateObserver from 'src/StateObserver';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { chunkArray } from 'src/replicant/utils/array';
import { assertNever } from 'src/replicant/utils';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import { Feature } from 'src/replicant/analytics';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import * as Visit from 'src/replicant/visit';
import { getTutorialController } from '../tutorial/TutorialController';
import TutorialHand from '../components/shared/TutorialHand';
import { getPetSpeciesBucket } from 'src/replicant/pet/pet.getters';

import { AssetGroup } from '@play-co/timestep-core';

type EncyclopediaItem = {
  id: PetSpeciesID;
  state: 'unveiled' | 'veiled' | 'unlocked';
  notification: boolean;
  tier?: number;
  level?: number;
  quantity?: number;
};
type EncyclopediaRow = [EncyclopediaItem, EncyclopediaItem?, EncyclopediaItem?];

export default class PopupEncyclopedia extends PopupBasic {
  private scroll: Scroll<EncyclopediaRow>;
  private effect: MovieClip;
  private visiting: boolean = false;
  private hand?: TutorialHand;

  constructor(opts: { superview: View; close: VoidFunction }) {
    super({
      superview: opts.superview,
      close: () => opts.close(),
      width: uiConfig.popups.buruburu.width,
      height: 820,
      darkerBg: true,
      skipMessage: true,
      tracking: {
        feature: Feature.ENCYCLOPEDIA._,
        $subFeature: Feature.ENCYCLOPEDIA.POPUP._,
      },
      // To match the mock, we need to not create the icon in the first place
      titleIcon: {
        image: 'assets/ui/encyclopedia/icon/encyclopedia_header_icon.png',
        width: 90,
        height: 80,
      },
    });

    this.buttonClose.removeFromSuperview();

    this.scroll = new Scroll<EncyclopediaRow>({
      superview: this.box,
      createItem: this.createItem.bind(this),
      showBg: true,
      firstItemOffset: 45,
      margin: 15,
      rect: {
        x: (this.box.style.width - 512) * 0.5,
        y: 94,
        width: 512,
        height: 586,
      },
    });

    this.effect = new MovieClip({
      url: 'assets/care/evolve',
      offsetX: -248,
      offsetY: -270,
      centerAnchor: true,
      scale: 0.6,
    });

    new ButtonScaleViewWithText({
      ...uiConfig.buttons.cancel,
      superview: this.box,
      labelOffsetY: 0,
      labelOffsetX: -9,
      fontSize: 50,
      font: bitmapFonts('Body'),
      x: this.box.style.width / 2,
      y: 742,
      width: 228,
      height: 94.7,
      centerOnOrigin: true,
      localeText: () => i18n('basic.close'),
      onClick: async () => {
        sounds.sfx.play('cancel');
        return opts.close();
      },
      tracking: {
        feature: Feature.ENCYCLOPEDIA._,
        $subFeature: Feature.ENCYCLOPEDIA.POPUP.BACK,
      },
    });

    this.createBanner();
  }

  protected getAssetsGroup() {
    return new AssetGroup([
      'assets/ui/encyclopedia/icon/encyclopedia_header_icon.png',
      'assets/ui/encyclopedia/banner/blue_banner.png',
      'assets/ui/shop/frames/scroll_cell.png',
      'assets/ui/encyclopedia/frames/outline.png',
      'assets/ui/gacha/icons/star.png',
      'assets/ui/encyclopedia/banner/notifiaction_ribbon.png',
      'assets/ui/encyclopedia/banner/duplicate_indicator.png',
      'assets/ui/encyclopedia/frames/level_frame.png',
      {
        url: 'assets/care/evolve',
        loadMethod: MovieClip.animationLoader,
      },
    ]);
  }

  public init(opts: {}) {
    const { user } = StateObserver.getState();
    this.visiting = Visit.getters.isVisiting(user);

    super.init({
      title: i18n('encyclopedia.popup.title'),
    });

    const {
      ruleset: { species },
      getters,
    } = Encyclopedia;
    let items = species.map((id) => {
      const species = id as PetSpeciesID;

      const visiteeState = 'veiled';
      const state = getters.getSpeciesState(user, id);
      const notification = this.visiting
        ? false
        : getters.getSpeciesNotification(user, id);

      return {
        id: species,
        state,
        notification,
      };
    });

    const rows = chunkArray(items, 3) as EncyclopediaRow[];

    this.scroll.setItems(rows);

    void this.showNewPetAnimation(items, rows);

    StateObserver.invoke.clearEncyclopediaNotifications();
  }

  /**
   * Show new pet animation
   */
  private async showNewPetAnimation(
    items: EncyclopediaItem[],
    rows: EncyclopediaRow[],
  ) {
    const firstNewPet = this.getNewPet(items)!;

    const petIcon = await this.scrollToPet(firstNewPet, rows);

    if (!petIcon) return;

    const tutorialTriggered = await getTutorialController().handleEvent(
      'newCostumeCollected',
    );

    if (!tutorialTriggered) return;

    // Create hand
    const hand = new TutorialHand({});
    petIcon.addSubview(hand);
    hand.fadeIn(50, 50, false);
    this.hand = hand;
  }

  /**
   * Return new pet item
   */
  private getNewPet(items: EncyclopediaItem[]) {
    return items.find((item) => item.notification);
  }

  private getPetById(items: EncyclopediaItem[], species: PetSpeciesID) {
    return items.find((item) => item.id === species);
  }

  /**
   * Scroll to the first new pet
   */
  private async scrollToPet(
    item: EncyclopediaItem,
    row: EncyclopediaRow[],
    useVFX: boolean = true,
  ) {
    const index = row.findIndex((r) => r.includes(item));

    if (index === -1) return null;

    const position = this.scroll.getElementPosition(index);
    const y = Math.max(0, position - 200);
    const rowViews = this.scroll.getView().getContentView().getSubviews();
    const view = rowViews[index].getSubviews().find((v) => v.tag === item.id)!;
    const pet = view
      .getSubviews()[0]
      .getSubviews()
      .find((i) => i.tag === 'pet')!;

    if (useVFX) {
      // Hide pet
      pet.setFilter(this.getFilter('veiled')!);
    }

    // Scroll to the pet
    await this.scroll.scrollTo(0, y, animDuration * 4);

    if (!useVFX) {
      return view.getSubviews()[0];
    }

    // Play sound only for unlocked pets
    if (item.state === 'unlocked') {
      sounds.sfx.play('aplause');
    }

    // Show VFX
    view.addSubview(this.effect);
    this.effect
      .playAsync('evolution_vfx_anims_final')
      .then(() => view.removeSubview(this.effect));

    // Wait a bit before reveal pet
    await waitForItPromise(animDuration * 5);

    // Set actual pet state
    pet.setFilter(this.getFilter(item.state)!);

    return view.getSubviews()[0];
  }

  /**
   * Return filter for pet depend on status
   */
  private getFilter(state: 'unveiled' | 'veiled' | 'unlocked') {
    switch (state) {
      case 'veiled':
        return new MultiplyFilter(new Color('rgba(255, 255, 255, 255)'));
      case 'unveiled':
        return new MultiplyFilter(new Color(`rgba(255, 255, 255, 155)`));
      case 'unlocked':
        return null;
      default:
        assertNever(state);
    }
  }

  private createBanner() {
    new ImageScaleView({
      superview: this.box,
      image: 'assets/ui/encyclopedia/banner/blue_banner.png',
      width: 509,
      height: 50,
      x: this.box.style.width / 2,
      y: 120,
      centerOnOrigin: true,
    });
  }

  private createItem(superview: View, _: number, row: EncyclopediaRow) {
    const view = new FlexBoxView({
      superview,
      width: superview.style.width,
      height: 160,
      flex: {
        paddingHorizontal: 7,
      },
    });

    row.forEach((value: EncyclopediaItem | undefined) => {
      if (!value) {
        return;
      }

      const id = value.id;
      const state = value.state;
      const notification = value.notification;
      const tier = value.tier;
      const level = value.level;
      const quantity = value.quantity;

      const setters: {
        setLevel?: (x: number) => void;
        setDuplicates?: (x: number) => void;
      } = {};

      const button = new ButtonScaleView({
        tag: id,
        superview: view,
        width: 160,
        height: 160,
        pressedOffsetY: 4,
        tracking: {
          feature: Feature.ENCYCLOPEDIA._,
          $subFeature: Feature.ENCYCLOPEDIA.POPUP.DETAILS,
        },
        onClick: async () => {
          sounds.sfx.play('ok');
          this.hand?.removeFromSuperview();
          this.hand = undefined;

          await openPopupPromise('popupEncyclopediaDetails', {
            species: id,
          });
        },
      });

      // Disable button if pet is not unlocked or unveiled
      button.setDisabled(state !== 'unlocked');

      const frame = new ImageScaleView({
        superview: button,
        image: 'assets/ui/shop/frames/scroll_cell.png',
        scaleMethod: '9slice',
        width: 160,
        height: 160,
        x: (superview.style.width - 485) * 0.5,
        sourceSlices: {
          horizontal: { left: 55, right: 55 },
          vertical: { top: 56.5, bottom: 56.5 },
        },
      });

      const node = getFlexNode(frame);
      node.updateStyle({
        flexShrink: 0,
        marginLeft: 5,
      });

      const image = new ImageScaleView({
        tag: 'pet',
        superview: frame,
        width: 100,
        height: 100,
        x: frame.style.width * 0.5,
        y: frame.style.height * 0.35,
        centerOnOrigin: true,
        scaleMethod: 'contain',
        image: `assets/ui/species/linepet_${id}.png`,
      });

      if (tier) {
        this.createItemRarity(frame, tier);
      }

      if (state !== 'unlocked') {
        image.setFilter(
          new MultiplyFilter(
            new Color(
              `rgba(255, 255, 255,  ${state === 'unveiled' ? 155 : 255})`,
            ),
          ),
        );
      }

      const { user } = StateObserver.getState();
      const evolutions = Pet.ruleset.evolutions[getPetSpeciesBucket(user)];
      const evolution = evolutions[id] as PetEvolution;
      const language = getCreativeSuffix();
      let petName = evolution?.[`name_${language}`];
      if (petName && language === 'jp') {
        petName = petName.replace('ぶるぶる科', '');
      }

      const petNameView = new ButtonScaleViewWithText({
        superview: frame,
        image: 'assets/ui/encyclopedia/frames/outline.png',
        scaleMethod: '9slice',
        sourceSlices: {
          horizontal: { left: 15, right: 15 },
          vertical: { top: 15, bottom: 15 },
        },
        font: 'Body',
        textColor: '#9C490D',
        fontSize: 20,
        width: 140,
        height: 30,
        x: frame.style.width * 0.5,
        y: frame.style.height - 35,
        tracking: {
          feature: Feature.ENCYCLOPEDIA._,
          $subFeature: Feature.ENCYCLOPEDIA.POPUP.DETAILS,
        },
        localeText: () => `${state === 'veiled' ? '???' : petName}`,
        centerOnOrigin: true,
      });

      if (notification) {
        this.createRibbon(frame, 'NEW');
      }
    });

    return view;
  }

  private createItemRarity(superview: View, count: number) {
    const stars = new FlexBoxView({
      superview,
      width: 140,
      height: 26,
      x: superview.style.width * 0.5,
      y: superview.style.height - 65,
      centerOnOrigin: true,
      flex: {
        flexDirection: 'row',
        justifyContent: 'center',
      },
    });

    for (let i = 0; i < count; i++) {
      new ImageScaleView({
        superview: stars,
        image: 'assets/ui/gacha/icons/star.png',
        width: 26,
        height: 26,
      });
    }
  }

  private createRibbon(superview: View, text: string) {
    const badge = new ImageScaleView({
      superview,
      width: 71,
      height: 32,
      x: -1,
      y: 7,
      image: 'assets/ui/encyclopedia/banner/notifiaction_ribbon.png',
    });

    new LangBitmapFontTextView({
      superview: badge,
      x: 10,
      width: 45,
      height: badge.style.height,
      align: 'center',
      verticalAlign: 'center',
      font: bitmapFonts('Body'),
      localeText: () => text,
      size: 21,
    });
  }

  private createDublicatesIndicator(superview: View, quantity: number) {
    const bg = new ImageScaleView({
      superview,
      width: 44,
      height: 43,
      x: superview.style.width - 44,
      y: 0,
      image: 'assets/ui/encyclopedia/banner/duplicate_indicator.png',
    });

    const label = new LangBitmapFontTextView({
      superview: bg,
      x: 5,
      y: -5,
      width: bg.style.width - 5,
      height: bg.style.height + 5,
      align: 'center',
      verticalAlign: 'center',
      font: bitmapFonts('Body'),
      localeText: () => quantity.toString(),
      size: 26,
    });

    return (quantity: number) => {
      label.localeText = () => quantity.toString();
    };
  }

  private createLevelIndicator(superview: View, level: number) {
    const bg = new ImageScaleView({
      superview,
      x: 12,
      y: superview.style.height / 2,
      width: 43,
      height: 44,
      centerOnOrigin: true,
      image: 'assets/ui/encyclopedia/frames/level_frame.png',
    });

    const label = new LangBitmapFontTextView({
      superview: bg,
      y: 2,
      width: bg.style.width - 4,
      height: bg.style.height - 4,
      align: 'center',
      verticalAlign: 'center',
      font: bitmapFonts('Body'),
      localeText: () => level.toString(),
      size: 20,
    });

    return (level: number) => {
      label.localeText = () => level.toString();
    };
  }
}
